import axios from 'axios';
import dayjs from 'dayjs';
import { API_BASE_URL, AUTH_BASE_URL } from '../config/serverApiConfig';
import { api, isLicenceRefusal } from './api';
import { messageFor } from './mfa.service';

/**
 * The appliance's licence.
 *
 * Two audiences, as on the server. The public calls, under `AUTH_BASE_URL`, are for the moment
 * nobody can sign in: the trial has ended, the API answers everything else with 402, and the key
 * has to be applied from the trial-ended screen. They use raw `axios`, like the recovery flow in
 * `mfa.service.ts`, because there is no session to send. The holder's calls go through `api()`
 * and are gated on `LICENCE`, which only a Superadmin holds and no workspace can hand out.
 *
 * Outside an appliance the status routes answer `{ enabled: false }` and activation answers 404.
 */

export { isLicenceRefusal };

export const LICENCE_PATH = '/licence';

export type LicenceState = 'trial' | 'licensed' | 'expired' | 'clock_rolled_back' | 'missing';

/** `GET /r/v2/auth/licence`. The appliance ID only while blocked: it is what the client sends SeeWard. */
export interface PublicLicenceStatus {
  enabled: boolean;
  blocked?: boolean;
  state?: LicenceState;
  appliance_id?: string;
  was_licensed?: boolean;
}

/** `GET /r/v2/licence`, for the licence holder. Timestamps are ISO 8601, UTC. */
export interface LicenceStatus {
  enabled: boolean;
  state?: LicenceState;
  blocked?: boolean;
  /** 14 days or fewer left. */
  expiring_soon?: boolean;
  days_left?: number | null;
  /** The later of the trial's end and the licence's. */
  expires_at?: string | null;
  appliance_id?: string | null;
  trial_started_at?: string | null;
  trial_expires_at?: string | null;
  licence_id?: string | null;
  licensed_to?: string | null;
  licence_expires_at?: string | null;
  activated_at?: string | null;
}

/** What a public activation discloses when it succeeds: the outcome, nothing more. */
export interface PublicActivation {
  state: LicenceState;
  blocked: boolean;
  expires_at: string | null;
  licensed_to: string | null;
}

/** A key pasted out of an email arrives wrapped and indented. The server strips whitespace too. */
export const normaliseKey = (raw: string): string => raw.replace(/\s+/g, '');

/** A form rule that stops an obviously truncated paste before it costs one of ten tries an hour. */
export const keyShapeRule = {
  validator: (_: unknown, value?: string) =>
    !value || normaliseKey(value).length >= 10
      ? Promise.resolve()
      : Promise.reject(new Error('That is too short to be a licence key.'))
};

export const getPublicLicenceStatus = () =>
  axios.get<PublicLicenceStatus>(`${AUTH_BASE_URL}licence`);

/** Accepted only while the appliance is blocked (409 `not_blocked` otherwise). Ten an hour. */
export const activateLicencePublic = (key: string) =>
  axios.post<PublicActivation>(`${AUTH_BASE_URL}licence/activate`, { 
    key: normaliseKey(key) 
  });

let cached: { at: number; promise: Promise<LicenceStatus> } | null = null;
const CACHE_MS = 60 * 1000;

/**
 * The holder's status, shared by the Settings tab and the reminder banner so one page does not
 * ask twice. A failed read is dropped rather than kept.
 */
export const fetchLicenceStatus = (force = false): Promise<LicenceStatus> => {
  if (!force && cached && Date.now() - cached.at < CACHE_MS) return cached.promise;
  const promise = api()
    .get<LicenceStatus>(`${API_BASE_URL}r/v2/licence`)
    .then((response) => response.data);
  cached = { at: Date.now(), promise };
  promise.catch(() => {
    if (cached?.promise === promise) cached = null;
  });
  return promise;
};

export const invalidateLicenceStatus = (): void => {
  cached = null;
};

/** Renew early, while signed in. */
export const activateLicence = async (key: string) => {
  const response = await api().post<LicenceStatus>(
    `${API_BASE_URL}r/v2/licence/activate`,
    { key: normaliseKey(key) }
  );
  invalidateLicenceStatus();
  return response;
};

export const formatLicenceDate = (iso?: string | null): string =>
  iso ? dayjs(iso).format('D MMMM YYYY') : 'Not set';

const CODE_MESSAGES: Record<string, string> = {
  invalid: 'That is not a valid SeeWard licence key. Paste the whole key: one line starting SW1.',
  not_blocked: 'This appliance is already licensed. Sign in, then apply the key under Settings, Licence.'
};

/**
 * Activation refusals carry `detail: { code, message }`. The server's message is written for the
 * person reading it: it names both appliance IDs for a key issued for another appliance, and both
 * dates for a key that does not extend the licence. So it is shown as it is, except for the codes
 * whose wording depends on where the key was pasted.
 */
export const licenceErrorMessage = (error: any): string => {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && typeof detail.code === 'string') {
    return CODE_MESSAGES[detail.code] ?? detail.message ?? 'The key was not accepted.';
  }
  return messageFor(error, {
    404: 'Licensing does not apply to this deployment.',
    429: 'Too many attempts. A key can be tried ten times an hour from one network. Try again later.'
  }, 'The key could not be applied. Please try again.');
};
